<template>
  <v-dialog
    v-model="dialog"
    max-width="680px"
    scrollable
    transition="dialog-bottom-transition"
    persistent
  >
    <v-card class="rounded-xl overflow-hidden">
      <!-- Header -->
      <v-toolbar color="error" dark flat class="rounded-t-xl">
        <v-btn icon dark @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title class="font-weight-bold pl-0">
          <v-icon left>mdi-account-off</v-icon>
          บันทึกการลาออก / พ้นสภาพ
        </v-toolbar-title>
      </v-toolbar>

      <!-- Employee Summary Banner -->
      <div class="employee-banner pa-4 d-flex align-center" v-if="employee">
        <v-avatar color="red lighten-5" size="56" class="mr-4">
          <span class="error--text font-weight-bold text-h6">
            {{ employee.frist_name ? employee.frist_name.charAt(0) : '?' }}
          </span>
        </v-avatar>
        <div>
          <div class="font-weight-bold text-body-1">
            {{ employee.title_s }}{{ employee.frist_name }} {{ employee.last_name }}
          </div>
          <div class="grey--text text-body-2">
            <v-icon x-small class="mr-1">mdi-briefcase-outline</v-icon>
            {{ employee.name_postion }}
          </div>
          <div class="grey--text text-body-2">
            <v-icon x-small class="mr-1">mdi-identifier</v-icon>
            เลขที่ตำแหน่ง: {{ employee.id_position }}
          </div>
        </div>
        <v-spacer></v-spacer>
        <v-chip color="error" text-color="white" label small>
          <v-icon left x-small>mdi-alert</v-icon>
          ดำเนินการลาออก
        </v-chip>
      </div>

      <v-divider></v-divider>

      <v-card-text class="pa-0 bg-light-grey" style="max-height: 65vh; overflow-y: auto;">
        <v-form ref="resignForm" v-model="formValid" lazy-validation class="pa-4 pa-md-6">

          <!-- Section 1: การลาออก -->
          <v-card class="mb-4 rounded-lg" outlined>
            <v-card-title class="text-body-1 font-weight-bold py-3 px-4 red lighten-5">
              <v-icon left color="error" small>mdi-calendar-remove</v-icon>
              ข้อมูลการลาออก
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="pa-4">
              <v-row dense>
                <!-- เหตุผลในการลา -->
                <v-col cols="12">
                  <v-select
                    v-model="resignData.resignation_reason_type"
                    :items="resignReasonTypes"
                    label="ประเภทการพ้นสภาพ *"
                    outlined
                    dense
                    :rules="[v => !!v || 'กรุณาเลือกประเภท']"
                    class="rounded-input"
                    prepend-inner-icon="mdi-format-list-bulleted-type"
                    @change="onReasonTypeChange"
                  ></v-select>
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="resignData.remark"
                    label="เหตุผลในการลาออก / รายละเอียดเพิ่มเติม *"
                    outlined
                    dense
                    rows="3"
                    counter="500"
                    maxlength="500"
                    :rules="[v => !!v || 'กรุณากรอกเหตุผล']"
                    class="rounded-input"
                    prepend-inner-icon="mdi-text"
                    :placeholder="reasonPlaceholder"
                  ></v-textarea>
                </v-col>

                <!-- วันที่ลาออก -->
                <v-col cols="12" md="6">
                  <v-menu
                    v-model="datePicker.resignation"
                    :close-on-content-click="false"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="resignationDateFormatted"
                        label="วันที่มีผลลาออก *"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        outlined
                        dense
                        :rules="[v => !!v || 'กรุณาเลือกวันที่']"
                        class="rounded-input"
                        prepend-inner-icon="mdi-calendar-today"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="resignData.resignation_date"
                      locale="th"
                      @input="datePicker.resignation = false"
                    ></v-date-picker>
                  </v-menu>
                </v-col>

                <!-- วันที่คีย์ลาออก (auto-fill วันนี้) -->
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="resignationRecordedFormatted"
                    label="วันที่บันทึกการลาออก"
                    outlined
                    dense
                    readonly
                    disabled
                    class="rounded-input"
                    prepend-inner-icon="mdi-calendar-clock"
                    hint="บันทึกอัตโนมัติวันที่ปัจจุบัน"
                    persistent-hint
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Section 2: ข้อมูลสัญญา -->
          <v-card class="mb-4 rounded-lg" outlined>
            <v-card-title class="text-body-1 font-weight-bold py-3 px-4 orange lighten-5">
              <v-icon left color="warning" small>mdi-file-document-edit</v-icon>
              ข้อมูลสัญญาและการปฏิบัติงาน
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="pa-4">
              <v-row dense>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="resignData.contract_count"
                    label="จำนวนครั้งที่ทำสัญญา *"
                    type="number"
                    min="0"
                    outlined
                    dense
                    :rules="[v => v !== '' && v !== null && v !== undefined || 'กรุณากรอก', v => v >= 0 || 'ต้องมากกว่าหรือเท่ากับ 0']"
                    class="rounded-input"
                    prepend-inner-icon="mdi-counter"
                    suffix="ครั้ง"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="resignData.last_royal_decoration"
                    label="เครื่องราชอิสริยาภรณ์ล่าสุด"
                    outlined
                    dense
                    class="rounded-input"
                    prepend-inner-icon="mdi-medal"
                    placeholder="เช่น บ.ม., ต.ม., จ.ม."
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Section 3: วินัย -->
          <v-card class="mb-4 rounded-lg" outlined>
            <v-card-title class="text-body-1 font-weight-bold py-3 px-4 purple lighten-5">
              <v-icon left color="deep-purple" small>mdi-gavel</v-icon>
              ประวัติการกระทำผิดวินัย
              <v-chip x-small color="grey lighten-2" class="ml-2">ไม่บังคับ</v-chip>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="pa-4">
              <v-row dense>
                <v-col cols="12">
                  <v-textarea
                    v-model="resignData.disciplinary_offense"
                    label="ความผิดทางวินัย"
                    outlined
                    dense
                    rows="2"
                    counter="500"
                    maxlength="500"
                    class="rounded-input"
                    prepend-inner-icon="mdi-alert-octagon-outline"
                    placeholder="ระบุรายละเอียดความผิดทางวินัย (ถ้ามี)"
                  ></v-textarea>
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="resignData.disciplinary_history"
                    label="ประวัติทางวินัย"
                    outlined
                    dense
                    rows="2"
                    counter="500"
                    maxlength="500"
                    class="rounded-input"
                    prepend-inner-icon="mdi-history"
                    placeholder="ระบุประวัติการกระทำผิดทางวินัย (ถ้ามี)"
                  ></v-textarea>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Section 4: แนบไฟล์ -->
          <v-card class="rounded-lg" outlined>
            <v-card-title class="text-body-1 font-weight-bold py-3 px-4 blue lighten-5">
              <v-icon left color="info" small>mdi-paperclip</v-icon>
              เอกสารแนบ
              <v-chip x-small color="grey lighten-2" class="ml-2">ไม่บังคับ</v-chip>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="pa-4">
              <v-file-input
                v-model="resignFile"
                label="แนบหนังสือลาออก (PDF)"
                outlined
                dense
                prepend-icon=""
                prepend-inner-icon="mdi-file-pdf-box"
                accept=".pdf"
                class="rounded-input"
                :loading="isUploading"
                show-size
                hint="รองรับไฟล์ PDF เท่านั้น"
                persistent-hint
              ></v-file-input>

              <!-- Preview ชื่อไฟล์ -->
              <div
                v-if="resignData.resignation_file"
                class="d-flex align-center mt-2 pa-3 green lighten-5 rounded-lg"
              >
                <v-icon color="success" small class="mr-2">mdi-check-circle</v-icon>
                <span class="green--text text--darken-2 text-body-2">
                  อัปโหลดสำเร็จ: {{ resignData.resignation_file }}
                </span>
              </div>
            </v-card-text>
          </v-card>

          <!-- Warning Notice -->
          <v-alert
            type="warning"
            outlined
            dense
            class="mt-4 rounded-lg"
            icon="mdi-alert-circle-outline"
          >
            <strong>คำเตือน:</strong> เมื่อยืนยันการลาออก ระบบจะย้ายข้อมูลของ
            <strong>{{ employee ? (employee.title_s + employee.frist_name + ' ' + employee.last_name) : '' }}</strong>
            ไปยังฐานข้อมูลพนักงานพ้นสภาพ และเลขที่ตำแหน่งจะกลายเป็นตำแหน่งว่าง
            การดำเนินการนี้ <strong>ไม่สามารถยกเลิกได้</strong>
          </v-alert>

        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <!-- Actions -->
      <v-card-actions class="pa-4">
        <v-btn
          depressed
          rounded
          color="grey lighten-3"
          @click="closeDialog"
          :disabled="isSubmitting"
        >
          <v-icon left small>mdi-close</v-icon>
          ยกเลิก
        </v-btn>
        <v-spacer></v-spacer>

        <!-- Confirm Checkbox -->
        <v-checkbox
          v-model="confirmCheck"
          color="error"
          hide-details
          dense
          class="mr-3 mt-0"
          :label="`ยืนยันการลาออกของ ${employee ? employee.frist_name : ''}`"
        ></v-checkbox>

        <v-btn
          depressed
          rounded
          color="error"
          @click="submitResign"
          :loading="isSubmitting"
          :disabled="!formValid || !confirmCheck || isUploading"
          class="px-6"
        >
          <v-icon left small>mdi-account-off</v-icon>
          ยืนยันการลาออก
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'ResignDialog',

  props: {
    value: {
      type: Boolean,
      default: false
    },
    employee: {
      type: Object,
      default: null
    },
    apiKey: {
      type: String,
      default: 'HRvec2021'
    }
  },

  data() {
    return {
      formValid: false,
      isSubmitting: false,
      isUploading: false,
      confirmCheck: false,
      resignFile: null,

      datePicker: {
        resignation: false
      },

      resignData: {
        resignation_reason_type: '',
        remark: '',
        resignation_date: new Date().toISOString().substr(0, 10),
        resignation_recorded_at: new Date().toISOString(),
        contract_count: 0,
        disciplinary_offense: '',
        disciplinary_history: '',
        last_royal_decoration: '',
        resignation_file: ''
      },

      resignReasonTypes: [
        'ลาออกด้วยความสมัครใจ',
        'ครบกำหนดสัญญาจ้าง',
        'เกษียณอายุ',
        'ถูกเลิกจ้าง',
        'โอนย้าย',
        'ถูกไล่ออก / ปลดออก (วินัย)',
        'เสียชีวิต',
        'อื่น ๆ'
      ]
    }
  },

  computed: {
    dialog: {
      get() { return this.value },
      set(val) { this.$emit('input', val) }
    },

    resignationDateFormatted() {
      return this.formatThaiDate(this.resignData.resignation_date)
    },

    resignationRecordedFormatted() {
      return this.formatThaiDateTime(new Date())
    },

    reasonPlaceholder() {
      const placeholders = {
        'ลาออกด้วยความสมัครใจ': 'ระบุเหตุผลส่วนตัว เช่น ย้ายถิ่นฐาน, เปลี่ยนงาน...',
        'ครบกำหนดสัญญาจ้าง': 'สัญญาจ้างสิ้นสุด ไม่ต่อสัญญา...',
        'เกษียณอายุ': 'ครบอายุเกษียณตามระเบียบ...',
        'ถูกเลิกจ้าง': 'ระบุเหตุผลการเลิกจ้าง...',
        'โอนย้าย': 'โอนย้ายไปหน่วยงาน...',
        'ถูกไล่ออก / ปลดออก (วินัย)': 'ระบุมติคณะกรรมการ / คำสั่ง...',
        'เสียชีวิต': 'ระบุวันที่เสียชีวิต / รายละเอียด...',
        'อื่น ๆ': 'ระบุรายละเอียดเพิ่มเติม...'
      }
      return placeholders[this.resignData.resignation_reason_type] || 'ระบุเหตุผลในการลาออก...'
    }
  },

  watch: {
    value(val) {
      if (val) {
        this.resetForm()
      }
    }
  },

  methods: {
    onReasonTypeChange() {
      this.resignData.remark = ''
    },

    async submitResign() {
      if (!this.$refs.resignForm.validate()) {
        this.$emit('snackbar', { message: 'กรุณากรอกข้อมูลให้ครบถ้วน', color: 'warning' })
        return
      }

      if (!this.confirmCheck) {
        this.$emit('snackbar', { message: 'กรุณายืนยันการดำเนินการ', color: 'warning' })
        return
      }

      this.isSubmitting = true

      try {
        // Step 1: Upload ไฟล์ถ้ามี
        if (this.resignFile) {
          await this.uploadResignFile()
        }

        // Step 2: ส่งข้อมูลการลาออก
        const payload = {
          ApiKey: this.apiKey,
          id_no: this.employee.id_no,
          ...this.resignData,
          resignation_recorded_at: new Date().toISOString().slice(0, 19).replace('T', ' ')
        }

        const result = await this.$http.post('government_employee_resign.php', payload)

        if (result.data && result.data.status) {
          this.$emit('snackbar', { message: 'บันทึกการลาออกสำเร็จ', color: 'success' })
          this.$emit('resigned')
          this.closeDialog()
        } else {
          this.$emit('snackbar', { message: result.data.message || 'เกิดข้อผิดพลาด', color: 'error' })
        }
      } catch (error) {
        this.$emit('snackbar', { message: 'เกิดข้อผิดพลาด: ' + error.message, color: 'error' })
      } finally {
        this.isSubmitting = false
      }
    },

    async uploadResignFile() {
      this.isUploading = true
      try {
        const idCard = this.employee.id_card || 'unknown'
        const timestamp = Date.now().toString(16)
        const random = Math.random().toString(16).substring(2, 8)
        const ext = this.resignFile.name.split('.').pop()
        const filename = `resign_${timestamp}${random}_${idCard}.${ext}`

        const formData = new FormData()
        formData.append('ApiKey', this.apiKey)
        formData.append('file', this.resignFile)
        formData.append('filename', '../HRemployee_resign/' + filename)

        const result = await this.$http.post('uploadfile.php', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })

        if (result.data && result.data.status) {
          this.resignData.resignation_file = filename
        } else {
          throw new Error('Upload failed')
        }
      } finally {
        this.isUploading = false
      }
    },

    closeDialog() {
      this.dialog = false
      this.resetForm()
    },

    resetForm() {
      this.confirmCheck = false
      this.resignFile = null
      this.resignData = {
        resignation_reason_type: '',
        remark: '',
        resignation_date: new Date().toISOString().substr(0, 10),
        resignation_recorded_at: new Date().toISOString(),
        contract_count: 0,
        disciplinary_offense: '',
        disciplinary_history: '',
        last_royal_decoration: '',
        resignation_file: ''
      }
      if (this.$refs.resignForm) {
        this.$refs.resignForm.resetValidation()
      }
    },

    formatThaiDate(dateStr) {
      if (!dateStr) return ''
      const months = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']
      const d = new Date(dateStr)
      return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear() + 543}`
    },

    formatThaiDateTime(date) {
      const months = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']
      const hh = String(date.getHours()).padStart(2, '0')
      const mm = String(date.getMinutes()).padStart(2, '0')
      return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear() + 543} ${hh}:${mm} น.`
    }
  }
}
</script>

<style scoped>
.employee-banner {
  background: linear-gradient(135deg, #fff5f5 0%, #fff 100%);
  border-bottom: 1px solid #ffcdd2;
}

.bg-light-grey {
  background-color: #f9fafb;
}

.rounded-input >>> .v-input__slot {
  border-radius: 8px !important;
}
</style>